<template>
  <div class="participant-list-component">
    <div class="participant-list-component-header">
      <template v-if="!isShowSearchForm">
        <div class="controls">
          <div class="control sort">
            <button class="control-text title" 
                    @click="toggleControlModal('sort')">{{ $t("user.parti.sort") }}</button>
            <GrmMenu
                class="control-modal"
                :open.sync="isShowModal.sort"
                :list="sortControlList"
            />
          </div>
          <div v-if="hasEmceeRole()" class="control auth">
            <button class="control-text title" 
                    @click="toggleControlModal('auth')">{{ $t("user.parti.auth") }}</button>
            <GrmMenu
                text-align="left"
                class="control-modal auth"
                :open.sync="isShowModal.auth"
                :list="authControlList"
            />
          </div>
        </div>
        <div v-if="lmsUserList.length > 0" class="controls items-center">
          <button class="text-xs px-2 py-1 bg-white font-bold border rounded border-gray-300" @click="onSendSMS('all')">{{ $t("user.parti.allSendSms") }}</button>
        </div>
        <div class="search">
          <button class="search-button" 
                  @click="toggleSearchForm" />
        </div>
      </template>
      <template v-else>
        <div class="search-form">
          <div class="search-input-wrapper">
            <input type="text" class="search-input" :placeholder="$t('user.parti.searchName')"
                   ref="searchInput"
                   @input="searchRoomUser($event.target.value)"/>
          </div>
          <button class="search-cancel" 
                  @click="toggleSearchForm"
          >{{ $t("common.cancel") }}</button>
        </div>
      </template>
    </div>
    <div class="participant-list-component-body" 
         ref="participantList"
         @drop="onDrop"

         @dragenter.prevent
         @dragend="onDragEnd"
         @dragstart.prevent
         @dragover.prevent
         @dragleave.prevent
         @drop.prevent.stop
    >
      <div class="h-full overflow-y-auto">
        <ul class="room-user-list"
            :class="{clickable: hasEmceeRole()}">
          <li class="room-user-item"
              :data-userId="participant.userId"
              :draggable="!isWebinarLive && hasEmceeRole() && !Device.isMobile()"
              @dragstart="event => onDragStart(event, participant)"
              v-for="participant in filteredParticipants" :key="participant.id">
            <div class="room-user-join-device border-l-4"
                 :class="[participant.deviceType, {'border-primary': isP2PCallType || participant.layoutSeqNum > -1}]"
                 v-tooltip.top="isP2PCallType || participant.layoutSeqNum > -1 ? $t('user.parti.video.receiving') : $t('user.parti.video.reject')"
            ></div>
            <div class="room-user-info">
              <GrmIcon icon="opener" class="mr-1"
                       pointerEvents="auto"
                       v-if="isOpener(participant.userId)"
                       v-tooltip.top="$t('common.opener')"
              />
              <span class="room-user-nickname"
										v-tooltip.top="participant.nickname"
                    :class="{ me: isMe(participant.userId) }"
                    @click="changeUserNickname(participant)">{{ participant.nickname }}</span>
              <GrmMark class="ml-1 grm-mark"
                       :data-user-role="participant.roomUserRole.roleId"
              >{{ userRoleName(participant.roomUserRole.roleId, true) }}</GrmMark>
            </div>
            <button class="room-user-device screen"
                    :class="[{ none: !hasDeviceRole(participant, 'screen') }
                             ,{ active: participant.userId === getScreenShareUser?.userId}]"
                    @click="onPermissionChangeClick(participant, 'screen')"
                    v-tooltip.top="deviceTooltip(participant, 'screen')"></button>
            <button class="room-user-device camera"
                    :class="[participant.cameraStatus, { none: !hasDeviceRole(participant, 'camera') }]"
                    @click="onPermissionChangeClick(participant, 'camera')"
                    v-tooltip.top="deviceTooltip(participant, 'camera')"></button>
            <button class="room-user-device mic"
                    :class="[participant.micStatus, { none: !hasDeviceRole(participant, 'mic') }]"
                    @click="onPermissionChangeClick(participant, 'mic')"
                    v-tooltip.top="deviceTooltip(participant, 'mic')"></button>
            <button class="room-user-device draw"
                    :class="{ none : !participant.roomUserRole.draw }"
                    @click="onPermissionChangeClick(participant, 'draw')"
                    v-tooltip.top="deviceTooltip(participant, 'draw')"></button>
            <div class="room-user-more"
                 v-if="hasEmceeRole()"
            >
              <button class="more-button"
                      v-tooltip.top="deviceTooltip(participant, 'more')"
                      @click="toggleUserMore(participant)" />
              <transition name="fade">
                <div v-if="isShowMoreModal[participant.userId]" ref="moreModal" class="room-user-more-modal"
                     :class="{ fixed: isFixedMoreModal }"
                     v-click-outside="() => { toggleUserMore(participant) }">
                  <ul class="room-user-role-list">
                    <li class="room-user-role-item"><button class="room-user-role-button"
                                                            :class="{ selected: participant.roomUserRole.roleId === 'speaker' }"
                                                            @click="setRoleId(participant,'speaker')">{{ userRoleName('speaker') }}</button></li>
                    <li class="room-user-role-item"><button class="room-user-role-button"
                                                            :class="{ selected: participant.roomUserRole.roleId === 'emcee' }"
                                                            @click="setRoleId(participant,'emcee')">{{ userRoleName('emcee') }}</button></li>
                    <li class="room-user-role-item"><button class="room-user-role-button"
                                                            :class="{ selected: participant.roomUserRole.roleId === 'participant' }"
                                                            @click="setRoleId(participant,'participant')">{{ userRoleName('participant') }}</button></li>
                    <li v-if="!isMe(participant.userId)" class="room-user-role-item"><button class="room-user-role-button kick" @click="kickUser(participant)">{{ $t("user.parti.kickUser.title") }}</button></li>
                  </ul>
                  <div class="room-user-browser-info">
                    <p class="room-user-browser-info-text">{{ osInfo(participant) }}</p>
                    <p class="room-user-browser-info-text">{{ clientInfo(participant) }}</p>
                    <p v-if="participant.deviceModel" class="room-user-browser-info-text">{{ participant.deviceModel }}</p>
                    <p v-if="participant.networkType" class="room-user-browser-info-text">{{ participant.networkType }}</p>
                  </div>
                </div>
              </transition>
            </div>
          </li>
          <template v-if="hasEmceeRole()">
            <li v-for="lmsUser in lmsUserList"
                :key="lmsUser.userId"
                class="room-user-item bg-gray-200"
            >
              <div class="room-user-info px-2 flex justify-between items-center">
              <span class="text-sm font-bold text-gray-400" style="font-size: 12px;">
                {{ lmsUser.username }}
              </span>
                <!-- <GrmBtn size="xs" @click="onSendSMS(lmsUser)">SMS 발송</GrmBtn> -->
                <button class="text-xs px-2 py-1 bg-white font-bold border rounded border-gray-300" @click="onSendSMS(lmsUser)">{{ $t("user.parti.sendSms") }}</button>
              </div>
            </li>
          </template>
        </ul>
      </div>
    </div>
  </div>
</template>
<script>
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import {throttle} from "lodash";
import {Device} from "@/utils"

import {Room} from "@/core/api/room.api";

import RoomUserChangeNickname from "@/components/Room/User/RoomUserChangeNickname";
import GrmResetBtn from "@/components/Common/button/GrmResetBtn.vue";
import RoomLmsSMS from "@/components/Room/lms/RoomLmsSMS.vue";
import GrmMenu from '@/components/Common/menu/GrmMenu';
import GrmMark from "@/components/Common/mark/GrmMark";
import GrmIcon from "@/components/Common/icon/GrmIcon";
import GrmBtn from "@/components/Common/button/GrmBtn.vue";

export default {
  name: "RoomList",
  components: {
    GrmIcon,
    GrmMark,
    GrmMenu,
    // GrmResetBtn,
  },
  created() {
    this.$roomApi.addEventListeners((this._Events = _Events()), this)
  },
  watch: {
    isShowMoreModal: {
      deep: true,
      handler(values) {
        this.$nextTick(() => {
          if (Object.values(values).some(val => val)) {
            this.resize()
          }
        })
      }
    },
    search(val){
      if(val?.length>0){
        this.filteredParticipants.throttle.cancel();
        this.filteredParticipants.search = '';
      }else{
        this.filteredParticipants.throttle(val);
      }
    }
  },
  data() {
    return {
      Device,
      orderBy: 'role',
      isDragging: false,
      isShowModal: {
        sort: false,
        auth: false,
      },
      userFilter: {
        throttle: null,
        text: '',
      },
      isFixedMoreModal: false,
      isShowSearchForm: false,
      isShowMoreModal: {},
    }
  },
  mounted() {
    window.addEventListener('resize', throttle(this.resize, 1000 / 30, {
      leading: false,
    }))
    this.userFilter.throttle = throttle(
        str => {
          this.userFilter.text = str
        },
        1000,
        {
          leading: false,
        })
  },
  methods: {
    ...mapActions('lms', [
      'getLmsUserList'
    ]),
    ...mapMutations('user', [
       'SET_ALL_USER_LAYOUT_OUT',
       'SET_USER_LAYOUT_IN',
    ]),
    resize() {
      if (!this.$refs.moreModal?.length) {
        return
      }

      const { bottom: buttonBottom } = this.$refs.moreModal[0].parentElement.getBoundingClientRect()
      const { top: modalTop, bottom: modalBottom } = this.$refs.moreModal[0].getBoundingClientRect()
      const { bottom: listBottom } = this.$refs.participantList.getBoundingClientRect()

      const isListBottomOver = listBottom < modalBottom + 12
      if (isListBottomOver) {
        this.isFixedMoreModal = true;
      }

      const isButtonBottomOver = buttonBottom < modalTop - 12
      if (isButtonBottomOver) {
        this.isFixedMoreModal = false;
      }
    },
    changeUserNickname(user) {
      if (this.manager || this.me.userId === user.userId) {
        this.$modal(RoomUserChangeNickname, {
          data: {
            user
          }
        })
      }
    },
    async toggleStatus(roomUser, deviceType) {
      this.toggleUserDeviceStatus({roomUser, deviceType})
    },
    onPermissionChangeClick (roomUser, deviceType) {
      if (this.isParticipantRole()) {
        this.$alert({
          text: this.$t('user.parti.error.permission'),
          onlyOk: true,
        });
        return;
      }

      if(deviceType === 'screen'){
        if(roomUser.userId === this.getScreenShareUser?.userId){
          return this.$alert({
            text: this.$t('user.parti.error.screenShare'),
            onlyOk: true,
          });
        }
      }
      this.onPermissionChange({roomUser, deviceType})
    },
    changeUserDeviceStatus(userId, type, deviceType, deviceStatus) {
      // TODO store에서 처리할 것
      switch (deviceType) {
        case 'camera':
        case 'mic':
          if (type === 'role') {
            Room.User.changeUserRole(userId, deviceType, deviceStatus)
          } else {
            Room.User.changeUserDeviceStatus(userId, deviceType, deviceStatus)
          }
          break
      }
    },
    changeUserDeviceStatuses(type, deviceType, deviceStatus) {
      if (!this.hasEmceeRole(this.me)) {
        return;
      }
      switch (deviceType) {
        case 'camera':
        case 'mic':
          if (type === 'role') {
            Room.User.changeUserRoleAll(deviceType, deviceStatus)
          } else {
            Room.User.changeUserDeviceStatusAll(deviceType, deviceStatus)
          }
          break
        case 'draw':
          Room.User.changeUserRoleAll(deviceType, deviceStatus)
          break
      }
      // changeUserDeviceStatusAll(type, deviceType, deviceStatus)

      this.toggleControlModal('auth')
    },
    kickUser(roomUser) {
      this.$alert({
        title: this.$t('user.parti.kickUser.title'),
        text: this.$t('user.parti.kickUser.text'),
        ok: () => {
          this.kickRoomUser({userId: roomUser.userId})
        }
      })
      this.isShowMoreModal[roomUser.userId] = false
    },
    kickAll() {
      this.kickAllRoomUser()
      this.toggleControlModal('auth')
    },
    setRoleId(roomUser, roleId) {
      const {userId, nickname} = roomUser;
      this.$alert({
        text: this.$t('user.parti.changeRole', {'0': nickname, '1': this.userRoleName(roleId)}),
        ok: () => {
          this.changeRoomUserRoleId({userId, roleType: roleId})
          this.isShowMoreModal[userId] = false
        }
      })
    },
    selectOrderBy(orderBy) {
      this.orderBy = orderBy
      this.toggleControlModal('sort')
    },
    toggleControlModal(type) {
      this.isShowModal[type] = !this.isShowModal[type]
    },
    toggleSearchForm() {
      this.isShowSearchForm = !this.isShowSearchForm
      if (this.isShowSearchForm) {
        this.$nextTick(() => {
          this.$refs.searchInput.focus()
        })
      } else {
        this.userFilter.text = ''
      }
    },
    toggleUserMore(roomUser) {
      const { userId, roomUserRole } = roomUser
      if(!this.hasEmceeRole()) return;
      if (roomUserRole?.roleId === 'rtsp') {
        return;
      }
      this.isShowMoreModal[userId] === undefined
          ? this.$set(this.isShowMoreModal, userId, true)
          : this.isShowMoreModal[userId] = !this.isShowMoreModal[userId]
    },
    ...mapActions('user', [
      'toggleUserDeviceStatus',
      'onPermissionChange',
      'changeRoomUserRoleId',
      'kickRoomUser',
      'kickAllRoomUser',
      'sortUserList',
    ]),
    searchRoomUser(str) {
      if (!str) {
        this.userFilter.throttle.cancel()
        this.userFilter.text = '';
      }else{
        this.userFilter.throttle(str)
      }
    },
    onDragStart(e, {userId}) {
      if (!this.hasEmceeRole()) {
        return
      }
      e.stopPropagation()

      this.isDragging = true;
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.dropEffect = 'move'
      e.dataTransfer.setData('user_info', JSON.stringify({userId}))

      const img = new Image();
      img.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYEAQAAAAa7ikwAAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAAAmJLR0QAAKqNIzIAAAAJcEhZcwAAAGAAAABgAPBrQs8AAAAHdElNRQfmCxEDNCFmuQ4kAAAA+ElEQVRIx91TsQ2DMBB8QyqElEGyARVL0GUDChZgiUjMkBFoKA0dE6RMTY0Egr8UTkJEQmIjUJScZFkv///d+2yivwGumBtPwfr2YD8E3Ts1zf++B0CewxhS3uqFzlXMESaEEEREm3Gj28FUgamg1T24T6CrcHF8eo7gLAN2O+NG43cxXccMtC1wOIC32xUI+n7IqiogigBL39vPBF33/A/KEvA8ogeT54OZyLaHuCiI0pSorheaoGlUBrPagsBInx7B8QiEoco8nwHHMSaYMh1IEsCy1CpLNUUcGxBI+Z5g+KCA76vT0wm8WcDfV4J4vwe77irNX+ECtLmFUHd80+UAAAAldEVYdGRhdGU6Y3JlYXRlADIwMjItMTEtMTdUMDM6NTI6MzMrMDA6MDCdGSqVAAAAJXRFWHRkYXRlOm1vZGlmeQAyMDIyLTExLTE3VDAzOjUyOjMzKzAwOjAw7ESSKQAAACh0RVh0ZGF0ZTp0aW1lc3RhbXAAMjAyMi0xMS0xN1QwMzo1MjozMyswMDowMLtRs/YAAAAASUVORK5CYII=';
      e.dataTransfer.setDragImage(img, 0, 0);
    },
    onDragEnd(e) {
      e.stopPropagation()

      this.isDragging = false;
    },
    onDrop(e) {
      if (this.isDragging || !this.hasEmceeRole()) {
        return
      }
      let userInfo = e.dataTransfer.getData('user_info')
      if (!userInfo) {
        return
      }
      userInfo = JSON.parse(userInfo)

      e.preventDefault()
      e.stopPropagation()

      this.$roomApi.Call.Mixer.changeMixingVideoChannelByUserId({
        userId: userInfo.userId,
        channel: -1,
        fromChannel: userInfo.idx
      })
    },
    onSendSMS(target) {
      this.$modal(RoomLmsSMS, { data: { target } })
    }
  },
  computed: {
    ...mapGetters('user', [
      'isOpener',
      'hasSpeakerRole',
      'getScreenShareUser',
    ]),
    ...mapGetters('room', [
      'room',
    ]),
    ...mapGetters('call', [
      'isWebinarLive',
      'isP2PCallType',
    ]),
    ...mapState('lms', [
      'lmsUserList'
    ]),
    userRoleName() {
      return (userId, showOnlyEmcee = false) => {
        const roleId = ['speaker', 'emcee', 'participant'].includes(userId)
          ? userId
          : this.getRoomUser(userId)?.roomUserRole?.roleId ?? userId

        switch (roleId) {
          case 'speaker':
            return this.$t('common.speaker');
          case 'emcee':
            return this.$t('common.emcee');
          case 'participant':
            if(showOnlyEmcee) return '';
            return this.$t('common.participant');
          case 'rtsp':
            return 'RTSP';
          default:
            return 'UNKNOWN';
        }
      }
    },
    userRoleLevel() {
      return (roomUser) => {
        const {roomUserRole : {roleId}} = roomUser;
        switch (roleId) {
          case 'speaker':
            return 0;
          case 'emcee':
            return 1;
          case 'participant':
            return 2;
          default:
            return 3;
        }
      }
    },
    hasDeviceRole() {
      return (roomUser, device) => {
        const {roomUserRole} = roomUser;

        if(this.isWebinarLive && !this.hasSpeakerRole(roomUser)) {
          return false;
        }else{
          return roomUserRole[device];
        }
      }
    },
    deviceTooltip() {
      return (roomUser, device) => {
          const role = this.hasDeviceRole(roomUser, device);
          const status = roomUser[device + 'Status'] === 'on';

          switch (device) {
            case 'camera':
              if(role){
                return status ? this.$t('user.parti.camera.on') : this.$t('user.parti.camera.off')
              }else{
                return this.$t('user.parti.camera.noRole')
              }
            case 'mic':
              if(role){
                return status ? this.$t('user.parti.mic.on') : this.$t('user.parti.mic.off')
              }else{
                return this.$t('user.parti.mic.noRole')
              }
            case 'screen':
              if(role){
                return roomUser[device + 'Status'] ? this.$t('user.parti.screenSharing') : this.$t('user.parti.screenShare.hasRole')
              }else{
                return this.$t('user.parti.screenShare.noRole')
              }
            case 'draw':
                return role ? this.$t('user.parti.draw.hasRole') : this.$t('user.parti.draw.noRole')
            case 'more':
              return this.hasEmceeRole() ? this.$t('user.parti.more.hasRole') : this.$t('user.parti.more.noRole')
            default:
              return 'UNKNOWN'
          }
      }
    },
    manager() {
      return this.hasEmceeRole(this.me);
    },
    filteredParticipants() {
      return this.roomUserList.filter(p => {
        // 옵저버 제외
        if (p.roomUserRole.roleId === 'obs') {
          return false;
        }

        // 검색처리
        if (this.userFilter.text.length > 0) {
          return p.nickname.includes(this.userFilter.text);
        }

        return true;
      }).sort((a, b) => {
        if (this.isMe(a.userId)) {
          return -1
        }
        if (this.isMe(b.userId)) {
          return 1
        }

        if (this.isOpener(a.userId)) {
          return -1
        }
        if (this.isOpener(b.userId)) {
          return 1
        }

        if (this.orderBy === 'connected') {
          if (a.enterDate > b.enterDate) {
            return 1;
          }
          if (a.enterDate < b.enterDate) {
            return -1;
          }
          return 0;
        } else if (this.orderBy === 'nickname') {
          if (a.nickname > b.nickname) {
            return 1;
          }
          if (a.nickname < b.nickname) {
            return -1;
          }
          return 0;
        } else if (this.orderBy === 'username') {
          if (a.username > b.username) {
            return 1;
          }
          if (a.username < b.username) {
            return -1;
          }
          return 0;
        } else if (this.orderBy === 'role') {
          return this.userRoleLevel(a) - this.userRoleLevel(b)
        } else if (this.orderBy === 'layoutSeqNum') {
          if(b.layoutSeqNum < 0) return -1;
          return a.layoutSeqNum - b.layoutSeqNum
        }

        return 0;
      })
    },
    sortControlList(){
      return [
          {
              title: this.$t('user.parti.order.auth'),
              selected: () => {
                  return this.orderBy === 'role'
              },
              click: () => {
                  this.selectOrderBy('role')
              }
          },
          {
              title: this.$t('user.parti.order.connect'),
              selected: () => {
                  return this.orderBy === 'connected'
              },
              click: () => {
                  this.selectOrderBy('connected')
              }
          },
          {
              title: this.$t('user.parti.order.name'),
              selected: () => {
                  return this.orderBy === 'nickname'
              },
              click: () => {
                  this.selectOrderBy('nickname')
              }
          },
          {
              title: this.$t('user.parti.order.layout'),
              selected: () => {
                  return this.orderBy === 'layoutSeqNum'
              },
              click: () => {
                  this.selectOrderBy('layoutSeqNum')
              }
          },
      ]
    },
    authControlList() {
      return [
        {
          title: this.$t('user.parti.controlCameraOn'),
          click: () => {
            if (this.isWebinarLive) {
              this.$alert(this.$t('user.parti.error.webinarLive'))
              return
            }

            this.changeUserDeviceStatuses('device', 'camera', 'on')
          },
          disabled: this.isWebinarLive
        },
        {
          title: this.$t('user.parti.controlCameraOff'),
          click: () => {
            if (this.isWebinarLive) {
              this.$alert(this.$t('user.parti.error.webinarLive'))
              return
            }

            this.changeUserDeviceStatuses('device', 'camera', 'off')
          },
          disabled: this.isWebinarLive
        },
        {
          title: this.$t('user.parti.roleCameraOn'),
          click: () => {
            if (this.isWebinarLive) {
              this.$alert(this.$t('user.parti.error.webinarLive'))
              return
            }

            this.changeUserDeviceStatuses('role', 'camera', true)
          },
          disabled: this.isWebinarLive
        },
        {
          title: this.$t('user.parti.roleCameraOff'),
          click: () => {
            if (this.isWebinarLive) {
              this.$alert(this.$t('user.parti.error.webinarLive'))
              return
            }
            this.changeUserDeviceStatuses('role', 'camera', false)
          },
          disabled: this.isWebinarLive
        },
        { type: 'divider' },
        {
          title: this.$t('user.parti.controlMicOn'),
          click: () => {
            if (this.isWebinarLive) {
              this.$alert(this.$t('user.parti.error.webinarLive'))
              return
            }

            this.changeUserDeviceStatuses('device', 'mic', 'on')
          },
          disabled: this.isWebinarLive
        },
        {
          title: this.$t('user.parti.controlMicOff'),
          click: () => {
            if (this.isWebinarLive) {
              this.$alert(this.$t('user.parti.error.webinarLive'))
              return
            }

            this.changeUserDeviceStatuses('device', 'mic', 'off')
          },
          disabled: this.isWebinarLive
        },
        {
          title: this.$t('user.parti.roleMicOn'),
          click: () => {
            if (this.isWebinarLive) {
              this.$alert(this.$t('user.parti.error.webinarLive'))
              return
            }

            this.changeUserDeviceStatuses('role', 'mic', true)
          },
          disabled: this.isWebinarLive
        },
        {
          title: this.$t('user.parti.roleMicOff'),
          click: () => {
            if (this.isWebinarLive) {
              this.$alert(this.$t('user.parti.error.webinarLive'))
              return
            }

            this.changeUserDeviceStatuses('role', 'mic', false)
          },
          disabled: this.isWebinarLive
        },
        { type: 'divider' },
        {
          title: this.$t('user.parti.controlDrawOn'),
          click: () => this.changeUserDeviceStatuses('role', 'draw', true)
        },
        {
          title: this.$t('user.parti.controlDrawOff'),
          click: () => this.changeUserDeviceStatuses('role', 'draw', false)
        },
        { type: 'divider' },
        {
          title: this.$t('user.parti.controlAllBan'),
          click: () => this.kickAll()
        },
      ]
    },
    osInfo () {
      return (participant) => {
        return `${participant.deviceType.toUpperCase()} - ${participant.deviceInfo}, ${participant.osInfo}`
      }
    },
    clientInfo () {
      return (participant) => {
        return participant?.deviceInfo
      }
    },
    ...mapGetters('user', [
      'me',
      'isMe',
      'getRoomUser',
      'roomUserList',
      'hasEmceeRole',
      'hasSpeakerRole',
      'hasUserInChannel',
      'isParticipantRole',
      'getUserDeviceStatus',
    ]),
  },
  filters: {
    displayRoleName(roleId) {
      switch (roleId) {
        case 'speaker':
          return this.$t('common.speaker');
        case 'emcee':
          return this.$t('common.emcee');
        case 'participant':
          return;
      }
      return 'UNKNOWN';
    }
  },
}

function _Events(){
  return {
    room_call_changeMixingLayout(e) {
      const {data} = e;
      let {channelSeqList} = data;
      if (data?.secondStream) {
        channelSeqList = data.secondStream.channelSeqList;
      }
      if (!channelSeqList) return;

      this.SET_ALL_USER_LAYOUT_OUT()
      channelSeqList.forEach((userId, seqNum) => {
        if (userId)
          this.SET_USER_LAYOUT_IN({userId, seqNum});
      })
    },
    room_call_changeCallType: async function (e) {
      const {callType} = e.data.settings;
      if(callType === 'p2p')
        this.SET_ALL_USER_LAYOUT_OUT()
    },
  }
}

</script>

<style lang="scss" scoped>
.participant-list-component {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.participant-list-component-header {
  height: 40px;
  border-bottom: 1px solid #ccc;
  background-color: #f8f8f8;
  display: flex;
  justify-content: space-between;
}
.controls {
  display: flex;
}
.control {
  display: flex;
  align-items: center;
  position: relative;
  &.sort {
    .control-button {
      padding: 0 40px;
    }
  }
  &.auth {
    .control-button {
      min-width: 150px;
      padding: 0 14px;
      text-align: left;
      &:hover {
        background-color: $mainColor;
        color: #fff;
      }
    }
  }
  &::before {
    content: "";
    width: 1px;
    height: 10px;
    background: #ccc;
    position: absolute;
    left: 0;
  }
  &:first-of-type::before {
    display: none;
  }
}
.control-text {
  font-size: 12px;
  &.title {
    height: 100%;
    padding: 0 20px 0 8px;
    background: url('~@/assets/images/arrow_down_triangular_filled.svg') no-repeat center right 6px / 8px auto;
  }
}
.control-modal {
  min-width: 108px;
  position: absolute;
  top: calc(100% - 10px);
  left: 4px;
  &.auth {
    min-width: 150px;
  }
}
.control-list {}
.control-item {
  &.divider {
    width: 100%;
    height: 1px;
    margin: 6px 0;
    background-color: #ddd;
  }
}
.control-item {}
.control-button {
  width: 100%;
  font-size: 12px;
  line-height: 36px;
  &:hover {
    background-color: #e8e8e8;
  }
  &.selected {
    background: url('~@/assets/images/check_blue.svg') no-repeat center left 16px / 12px auto;
  }
}
.search {}
.search-button {
  width: 40px;
  height: 100%;
  background: url('~@/assets/images/search.svg') no-repeat center / 14px auto;
}
.search-form {
  width: 100%;
  background-color: #fff;
  display: flex;
}
.search-input-wrapper {
  flex: 1;
}
.search-input {
  width: 100%;
  height: 100%;
  background: url('~@/assets/images/search.svg') no-repeat center left 10px / 14px auto;
  border: 0;
  padding-left: 34px;
  box-sizing: border-box;
  font-size: 12px;
  outline: none;
}
.search-cancel {
  height: 100%;
  padding: 0 12px;
  font-size: 12px;
}
.participant-list-component-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.room-user-list {
  &.clickable{
    .room-user-nickname {
      &:hover{
        font-weight: 700;
      }
      cursor: pointer;
    }
  }
  .room-user-item {
    height: 38px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    &[draggable="true"] {
      cursor: grab;
      &:hover{
        background-color: #DFEFFF;
      }
    }
  }
}
.room-user-join-device {
  width: 30px;
  height: 100%;
  &.pc {
    background: url('~@/assets/images/user_device_pc.svg') no-repeat center / 14px auto;
    //
    //&.layoutIn {
    //    background: url('~@/assets/images/user_device_pc_in.svg') no-repeat center / 14px auto
    //}
  }
  &.tablet {
    background: url('~@/assets/images/user_device_tablet.svg') no-repeat center / 14px auto
  }
  &.mobile {
    background: url('~@/assets/images/user_device_mobile.svg') no-repeat center / 14px auto
  }
}
.room-user-info {
  flex: 1;
  min-width: 0;
  padding-right: 6px;
  display: flex;
  align-items: center;
  &.opener {
    &::before{
      content: "";
      width: 20px;
      height: 24px;
      background: url('~@/assets/images/crown_yellow.svg') no-repeat center/14px auto;
      display: block;
      margin-left: -6px;
    }
  }
}
.room-user-nickname {
  min-width: 0;
  max-width: max-content;
  flex: 1;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &.me {
    color: $mainColor;
    font-weight: 700;
    cursor: pointer;
  }
}
.room-user-device {
  width: 26px;
  height: 100%;
  &.screen {
    background: url('~@/assets/images/screen.svg') no-repeat center / 14px auto;
    &.none {
      opacity: 0.3;
    }
    &.active {
      background: url('~@/assets/images/screen_share.svg') no-repeat center / 14px auto;
    }
  }
  &.camera {
    &.on {
      background: url('~@/assets/images/cam_on.svg') no-repeat center / 12px auto;
    }
    &.off {
      background: url('~@/assets/images/cam_off_red.svg') no-repeat center / 12px auto;
    }
    &.none {
      background: url('~@/assets/images/cam_off_gray.svg') no-repeat center / 12px auto;
    }
  }
  &.mic {
    &.on {
      background: url('~@/assets/images/mic_on.svg') no-repeat center / 12px auto;
    }
    &.off {
      background: url('~@/assets/images/mic_off_red.svg') no-repeat center / 12px auto;
    }
    &.none {
      background: url('~@/assets/images/mic_off_gray.svg') no-repeat center / 12px auto;
    }
  }
  &.draw {
    background: url('~@/assets/images/pen.svg') no-repeat center / 12px auto;
    &.none {
      background: url('~@/assets/images/pen_gray.svg') no-repeat center / 12px auto;
    }
  }
  &:hover {
    background-color: #eee !important;
  }
}
.room-user-more {
  height: 100%;
  position: relative;
}
.more-button {
  width: 36px;
  height: 100%;
  background: url('~@/assets/images/more_gray.svg') no-repeat 50% / 18px auto;
  &:hover {
    background-color: #eee !important;
  }
}
.room-user-more-modal {
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 4px 10px 30px rgba(0, 0, 0, 0.3);
  position: absolute;
  transform: translate(calc(-100% + 26px), -4px);
  overflow: hidden;
  z-index: 1;
  &.fixed {
    position: fixed;
    right: 16px;
    bottom: 10px;
    transform: initial;
  }
}
.room-user-role-list {
  display: flex;
}
.room-user-role-item {
  min-width: 60px;
  border-left: 1px solid #eee;
  &:first-of-type {
    border-left: 0;
  }
}
.room-user-role-button {
  width: 100%;
  height: 100%;
  padding: 0 10px;
  font-size: 12px;
  line-height: 36px;
  white-space: nowrap;
  &:hover {
    background-color: #f4f4f4;
  }
  &.selected {
    cursor: default;
    pointer-events: none;
    background-color: $mainColor;
    color: #fff;
  }
  &.kick {
    color: #f05;
    &:hover {
      background-color: rgba(255, 0, 85, 0.06);
      font-weight: 700;
    }
  }
}
.room-user-browser-info {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding: 12px;
  background-color: #f2f4f6;
  display: flex;
  flex-direction: column;
  gap: 4px 0;
}
.room-user-browser-info-text {
  font-size: 11px;
  white-space: nowrap;
}
.grm-mark{
  &[data-user-role="speaker"]{
    background-color: #fa5;
  }
  &[data-user-role="emcee"]{
    background-color: #bfbfc4;
  }
  &[data-user-role="rtsp"]{
    background-color: #bdd7ff;
  }
}
</style>